import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useAuth } from '../auth';
import { Loading } from './index';

/**
 * RequireAuth component - Simple authentication check
 * Only checks if user is logged in, doesn't check member type
 * 
 * @param {Object} props - Component props
 * @param {React.Component} props.component - Component to render if authenticated
 * @param {string|string[]} props.path - Path(s) to protect
 * @param {string} props.redirectTo - Path to redirect to if not authenticated
 */
export function RequireAuth({ 
  component: Component, 
  path,
  redirectTo = "/login",
  ...rest 
}) {
  const auth = useAuth();
  
  // Convert path to array if it's a single string
  const pathArray = Array.isArray(path) ? path : [path];
  
  // Check if authentication is still being resolved
  const isAuthResolving = !auth.user && !auth.session && typeof window !== 'undefined';
  
  // If multiple paths, create multiple routes
  if (pathArray.length > 1) {
    return (
      <>
        {pathArray.map((singlePath, index) => (
          <Route
            key={`${singlePath}-${index}`}
            path={singlePath}
            {...rest}
            render={props => {
              // Show loading only if auth is still resolving
              if (isAuthResolving) {
                return <Loading />;
              }
              
              // Check if user is logged in
              if (!auth.user && !auth.session) {
                return <Redirect to={redirectTo} />;
              }
              
              // User is authenticated, render component
              return <Component {...props} />;
            }}
          />
        ))}
      </>
    );
  }
  
  // Single path route
  return (
    <Route
      path={path}
      {...rest}
      render={props => {
        // Show loading only if auth is still resolving
        if (isAuthResolving) {
          return <Loading />;
        }
        
        // Check if user is logged in
        if (!auth.user && !auth.session) {
          return <Redirect to={redirectTo} />;
        }
        
        // User is authenticated, render component
        return <Component {...props} />;
      }}
    />
  );
}

